import * as React from 'react';
import {Avatar, Box, Card, CardContent, Chip, Divider, Stack, TextField, Typography} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import {useTranslation} from 'react-i18next';
import {ColumnDefinition, getItemTreatmentColors, getItemTreatmentLabelKey, RowData} from '../../../../hooks/useMonthlyChange';
import {getAvatarColors} from '../../../../utils/avatarUtils';

export interface MonthlyChangeDataTableMobileProps {
    basePayColumn: ColumnDefinition | null;
    visibleEditableColumns: ColumnDefinition[];
    rows: RowData[];
    getCellDisplayValue: (row: RowData, column: ColumnDefinition) => string;
    getEmployeeName: (row: RowData) => string;
    getEmployeeInitials: (row: RowData) => string;
    getBasePayDisplay: (row: RowData) => string;
    getColumnTreatmentSource: (column: ColumnDefinition) => { type?: string | null; taxBasis?: string | null };
    activeCellId: string | null;
    handleCellValueChange: (cellId: string, value: string) => void;
    handleCellActivate: (cellId: string) => void;
    isPeriodLocked: boolean;
}

export default function MonthlyChangeDataTableMobile({
    basePayColumn,
    visibleEditableColumns,
    rows,
    getCellDisplayValue,
    getEmployeeName,
    getEmployeeInitials,
    getBasePayDisplay,
    getColumnTreatmentSource,
    activeCellId,
    handleCellValueChange,
    handleCellActivate,
    isPeriodLocked,
}: Readonly<MonthlyChangeDataTableMobileProps>): React.ReactElement {
    const {t} = useTranslation();

    if (rows.length === 0) {
        return (
            <Box sx={{textAlign: 'center', py: 6}}>
                <SwapHorizIcon sx={{fontSize: 48, color: 'text.disabled', mb: 1}}/>
                <Typography variant="body1" color="text.secondary">{t('payroll.monthlyChange.noEmployeesFound')}</Typography>
                <Typography variant="body2" color="text.disabled">{t('payroll.monthlyChange.tryDifferentFilter')}</Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={1.5}>
            {rows.map((row) => {
                const name = getEmployeeName(row);
                const avatarColors = getAvatarColors(`${name}${row.id}`);
                return (
                    <Card key={row.id} variant="outlined" sx={{borderRadius: 1}}>
                        <CardContent sx={{textAlign: 'left'}}>
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                                <Stack direction="row" alignItems="center" spacing={1.5} sx={{minWidth: 0}}>
                                    <Avatar sx={{
                                        width: 36, height: 36,
                                        bgcolor: avatarColors.bg, color: avatarColors.color,
                                        fontSize: '0.875rem', fontWeight: 600, flexShrink: 0,
                                    }}>
                                        {getEmployeeInitials(row)}
                                    </Avatar>
                                    <Typography variant="subtitle2" fontWeight={600} noWrap>{name || '-'}</Typography>
                                </Stack>
                                {basePayColumn && (
                                    <Box sx={{textAlign: 'right', flexShrink: 0}}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {t('payroll.period.employees.header.basePay')}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={600}>
                                            {getBasePayDisplay(row) || '-'}
                                        </Typography>
                                    </Box>
                                )}
                            </Stack>

                            <Divider sx={{my: 1.5}}/>

                            <Stack spacing={0.5}>
                                {visibleEditableColumns.map((column) => {
                                    const cellId = `${row.id}-${column.id}`;
                                    const isActive = activeCellId === cellId;
                                    const value = getCellDisplayValue(row, column);
                                    const hasValue = value && value !== '-' && Number.parseFloat(value) !== 0;
                                    const treatmentSource = getColumnTreatmentSource(column);
                                    const colors = getItemTreatmentColors(treatmentSource);

                                    return (
                                        <Box
                                            key={cellId}
                                            onClick={() => !isPeriodLocked && !isActive && handleCellActivate(cellId)}
                                            sx={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                minHeight: 44, px: 0.5, borderRadius: 1, gap: 1,
                                                cursor: isPeriodLocked ? 'default' : 'pointer',
                                                backgroundColor: hasValue ? 'rgb(217, 243, 190)' : 'inherit',
                                            }}
                                        >
                                            <Stack direction="row" alignItems="center" spacing={1} sx={{minWidth: 0}}>
                                                <Typography variant="body2" color="text.secondary" noWrap>{column.name}</Typography>
                                                <Chip
                                                    label={t(getItemTreatmentLabelKey(treatmentSource))}
                                                    size="small"
                                                    sx={{height: 18, fontSize: '0.6rem', backgroundColor: colors.bg, color: colors.color}}
                                                />
                                            </Stack>

                                            {isActive ? (
                                                <TextField
                                                    size="small"
                                                    value={value}
                                                    variant="outlined"
                                                    disabled={isPeriodLocked}
                                                    onChange={(e) => handleCellValueChange(cellId, e.target.value)}
                                                    onBlur={() => handleCellActivate('')}
                                                    autoFocus
                                                    sx={{width: 110, flexShrink: 0, '& .MuiOutlinedInput-input': {py: '4px', px: '8px'}}}
                                                />
                                            ) : (
                                                <Typography variant="body2" fontWeight={hasValue ? 700 : 400} sx={{flexShrink: 0}}>
                                                    {value ? `${value} €` : '0,00 €'}
                                                </Typography>
                                            )}
                                        </Box>
                                    );
                                })}
                            </Stack>
                        </CardContent>
                    </Card>
                );
            })}
        </Stack>
    );
}
